import { useCallback } from 'react'
import { useAuthStore } from '@/store/authStore'
import { clearOrderCreationPersistedState } from '@/store/orderCreationStore'
import { clearSessionQueries } from '@/lib/queryClient'
import { finalizeLocalSessionEnd } from '@/lib/sessionCleanup'
import { tokenService } from '@/services/tokenService'
import { setSentryUser, clearSentryUser, addBreadcrumb } from '@/config/sentry'
import { identifyMixpanelUser, resetMixpanelUser } from '@/config/mixpanel'
import { trackEvent, setUserContext } from '@/lib/analytics'
import { trackEvent as trackFullEvent } from '@/utils/analytics'
import * as authAPI from '@/api/auth'
import type { PendingChallenge } from '@/types/auth'

export function useAuth() {
  const {
    user,
    accessToken,
    refreshToken,
    isAuthenticated,
    pendingChallenge,
    authSession,
    loading,
    error,
    setUser,
    setTokens,
    setPendingChallenge,
    setAuthSession,
    setLoading,
    setError,
  } = useAuthStore()

  /**
   * Start login process with phone number
   * Follows external dashboard auth guide format
   */
  const startLogin = useCallback(
    async (phoneNumber: string): Promise<{ success: boolean; message?: string }> => {
      // Add + prefix if not present (user should include country code)
      const phoneWithCountryCode = phoneNumber.startsWith('+') 
        ? phoneNumber 
        : `+${phoneNumber}`
      
      setLoading(true)
      setError(null)

      try {
        // Step 1: Begin auth session - send phone with country code
        const beginResult = await authAPI.beginAuth(phoneWithCountryCode, 'login')

        if (!beginResult.success) {
          setError(beginResult.message || 'Failed to start authentication')
          return { success: false, message: beginResult.message }
        }

        if (!beginResult.data) {
          setError('Invalid response from server')
          return { success: false, message: 'Invalid response from server' }
        }

        const beginData = beginResult.data
        const { session_id, requires_strategy_selection, available_strategies, strategy } = beginData

        // Check if strategy is already set to password (API key configured for password)
        if (strategy === 'password' && !requires_strategy_selection) {
          const errorMsg = 'API key is configured for password authentication. Please configure it for OTP SMS in the backend, or use password authentication.'
          setError(errorMsg)
          return { success: false, message: errorMsg }
        }

        // Step 2: Select strategy if required
        let selectedStrategy = strategy
        let finalSessionId = session_id
        
        if (requires_strategy_selection && available_strategies) {
          // For phone number login, prefer otp_sms strategy
          const otpSmsStrategy = available_strategies.find((s) => s.strategy === 'otp_sms')
          if (otpSmsStrategy) {
            const selectResult = await authAPI.selectStrategy(session_id, 'otp_sms')
            if (selectResult.success && selectResult.data) {
              selectedStrategy = selectResult.data.strategy || 'otp_sms'
              // Use the session_id from the select response if provided
              finalSessionId = selectResult.data.session_id || session_id
            } else {
              const errorMsg = selectResult.message || 'Failed to select OTP SMS strategy'
              setError(errorMsg)
              return { success: false, message: errorMsg }
            }
          } else {
            const errorMsg = 'OTP SMS strategy is not available. Available strategies: ' + available_strategies.map(s => s.strategy).join(', ')
            setError(errorMsg)
            return { success: false, message: errorMsg }
          }
        } else if (strategy && strategy !== 'otp_sms') {
          // Strategy is set but not otp_sms
          const errorMsg = `Current strategy is "${strategy}" but OTP SMS is required. Please configure the API key for OTP SMS in the backend.`
          setError(errorMsg)
          return { success: false, message: errorMsg }
        }

        // Store auth session
        const session = {
          session_id,
          identifier: phoneWithCountryCode, // Store with country code
          strategy: selectedStrategy,
          requires_strategy_selection: beginData.requires_strategy_selection,
          available_strategies: beginData.available_strategies,
          expires_at: beginData.expires_at,
          session_duration_seconds: beginData.session_duration_seconds,
        }
        setAuthSession(session)

        // Step 3a: Generate OTP challenge (before setting pendingChallenge)
        // Use finalSessionId (from select-strategy if it was called, otherwise original session_id)
        const generateResult = await authAPI.generateOTP(finalSessionId, phoneWithCountryCode)

        if (!generateResult.success) {
          const errorMsg = generateResult.message || 'Failed to send OTP'
          setError(errorMsg)
          // Don't set pendingChallenge if OTP generation failed
          return { success: false, message: errorMsg }
        }

        // Extract challenge_id from generateOTP response
        const challengeId = generateResult.data?.session_data?.challenge_id

        // Store pending challenge only after OTP is successfully generated
        const challenge: PendingChallenge = {
          session_id: finalSessionId, // Use the final session_id (may be from select-strategy)
          identifier: phoneWithCountryCode, // Store with country code
          strategy: selectedStrategy,
          challenge_id: challengeId, // Store challenge_id for verification
        }
        setPendingChallenge(challenge)

        // Analytics: OTP requested (dataLayer only, same as old project)
        trackEvent('auth_otp_requested', { user_id: 'pending', channel: 'otp_sms' })

        // Sentry breadcrumb
        addBreadcrumb({
          category: 'auth',
          message: 'OTP requested',
          level: 'info',
          data: { session_id: finalSessionId },
        })

        return { success: true }
      } catch (err: any) {
        const message = err?.message || err?.response?.data?.message || 'Unexpected error occurred'
        setError(message)
        // Clear pendingChallenge on error
        setPendingChallenge(null)
        return { success: false, message }
      } finally {
        setLoading(false)
      }
    },
    [setLoading, setError, setAuthSession, setPendingChallenge]
  )

  /**
   * Verify OTP code
   * Follows external dashboard auth guide format
   */
  const verifyOTP = useCallback(
    async (otp: string): Promise<{ success: boolean; message?: string }> => {
      setLoading(true)
      setError(null)

      try {
        if (!pendingChallenge) {
          setError('No pending authentication challenge')
          return { success: false, message: 'No pending authentication challenge' }
        }

        // Step 3b: Verify OTP credential
        const verifyResult = await authAPI.verifyOTP(
          pendingChallenge.session_id,
          pendingChallenge.identifier,
          otp,
          pendingChallenge.challenge_id // Pass challenge_id from generateOTP response
        )

        if (!verifyResult.success) {
          setError(verifyResult.message || 'OTP verification failed')
          return { success: false, message: verifyResult.message }
        }

        if (!verifyResult.data) {
          setError('Invalid response from server')
          return { success: false, message: 'Invalid response from server' }
        }

        const verifyData = verifyResult.data

        // Check if authentication is complete
        if (!verifyData.authentication_complete) {
          setError('Authentication not complete. Additional steps may be required.')
          return { success: false, message: 'Authentication not complete' }
        }

        // Extract tokens (support both formats: access_token/refresh_token or access/refresh)
        const accessToken =
          verifyData.access_token || (verifyData as any).access || ''
        const refreshToken =
          verifyData.refresh_token || (verifyData as any).refresh || ''

        if (!accessToken || !refreshToken) {
          setError('Tokens not received from server')
          return { success: false, message: 'Tokens not received from server' }
        }

        const previousUserId = useAuthStore.getState().user?.id

        // Save tokens
        await tokenService.saveTokens(accessToken, refreshToken)
        setTokens(accessToken, refreshToken)

        // New session token: drop all React Query cache (prior user's GraphQL/REST data)
        clearSessionQueries()

        // Prevent a one-frame render of the previous account while /me runs
        setUser(null)

        // Fetch full user profile
        const meResult = await authAPI.getMe()

        const newUserId =
          (meResult.success && meResult.data?.id) || verifyData.user_id || undefined

        if (
          previousUserId &&
          newUserId &&
          String(previousUserId) !== String(newUserId)
        ) {
          clearOrderCreationPersistedState()
        }

        if (meResult.success && meResult.data) {
          await tokenService.saveUserData(meResult.data)
          setUser(meResult.data)
        } else if (verifyData.user_id) {
          // If getMe fails but we have user_id, create minimal user object
          const minimalUser = {
            id: verifyData.user_id,
            phone_number: pendingChallenge.identifier,
            first_name: '',
            last_name: '',
          }
          await tokenService.saveUserData(minimalUser)
          setUser(minimalUser)
        }

        // Clear pending challenge
        setPendingChallenge(null)

        // ── Analytics: OTP verified + login success ──────────────────────

        const loggedInUser = meResult?.success && meResult.data
          ? meResult.data
          : null
        const userId = loggedInUser?.id || verifyData.user_id || ''
        const userRole = loggedInUser?.role || ''

        // Sentry: set user context
        setSentryUser({
          id: userId,
          username: loggedInUser?.phone_number || pendingChallenge.identifier,
          role: userRole,
          sessionId: pendingChallenge.session_id,
        })

        // Sentry breadcrumb
        addBreadcrumb({
          category: 'auth',
          message: 'OTP verified — login success',
          level: 'info',
          data: { user_id: String(userId) },
        })

        // GA4/GTM: user context + auth events (dataLayer + Mixpanel when configured)
        setUserContext(userId, userRole)
        trackFullEvent('auth_otp_verified', { user_id: String(userId), user_role: userRole })
        trackFullEvent('auth_login_success', {
          user_id: String(userId),
          user_role: userRole,
          login_method: 'otp_sms',
        })

        identifyMixpanelUser({
          id: userId,
          email: loggedInUser?.email ?? undefined,
        })

        return { success: true }
      } catch (err: any) {
        const message = err.message || 'Unexpected error occurred'
        setError(message)
        return { success: false, message }
      } finally {
        setLoading(false)
      }
    },
    [pendingChallenge, setLoading, setError, setTokens, setUser, setPendingChallenge]
  )

  /**
   * Resend OTP
   * Follows external dashboard auth guide format
   */
  const resendOTP = useCallback(async (): Promise<{ success: boolean; message?: string }> => {
    setLoading(true)
    setError(null)

    try {
      if (!authSession) {
        setError('No active authentication session')
        return { success: false, message: 'No active authentication session' }
      }

      // Regenerate OTP challenge
      const result = await authAPI.generateOTP(authSession.session_id, authSession.identifier)

      if (!result.success) {
        setError(result.message || 'Failed to resend OTP')
        return { success: false, message: result.message }
      }

      return { success: true }
    } catch (err: any) {
      const message = err.message || 'Unexpected error occurred'
      setError(message)
      return { success: false, message }
    } finally {
      setLoading(false)
    }
  }, [authSession, setLoading, setError])

  /**
   * Logout
   */
  const logout = useCallback(async (): Promise<void> => {
    setLoading(true)

    // ── Analytics: logout events ─────────────────────────────────────
    const currentUser = useAuthStore.getState().user
    const userId = currentUser?.id || ''
    const userRole = currentUser?.role || ''

    // GA4/GTM: auth_logout + clear user context (dataLayer only)
    trackEvent('auth_logout', {
      user_id: String(userId),
      user_role: userRole,
    })
    setUserContext(undefined, undefined)

    // Mixpanel + dataLayer (unified trackEvent from utils)
    trackFullEvent('Logout', { user_id: String(userId), user_role: userRole })
    resetMixpanelUser()

    // Sentry: clear user + breadcrumb
    addBreadcrumb({
      category: 'auth',
      message: 'User logged out',
      level: 'info',
      data: { user_id: String(userId) },
    })
    clearSentryUser()

    try {
      // Call logout API (invalidate tokens on server)
      await authAPI.logoutAll()
    } catch (err) {
      // Continue with local cleanup even if API call fails
      console.error('Logout API call failed:', err)
    } finally {
      await finalizeLocalSessionEnd('manual_logout')
      setLoading(false)
    }
  }, [setLoading])

  /**
   * Clear pending challenge (go back to phone step)
   */
  const clearPendingChallenge = useCallback(() => {
    setPendingChallenge(null)
    setError(null)
  }, [setPendingChallenge, setError])

  return {
    // State
    user,
    accessToken,
    refreshToken,
    isAuthenticated,
    pendingChallenge,
    authSession,
    loading,
    error,

    // Actions
    startLogin,
    verifyOTP,
    resendOTP,
    logout,
    clearPendingChallenge,
    setError,
  }
}
